import React, { useEffect, useMemo, useState } from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import EditOutlined from '@material-ui/icons/EditOutlined'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import AddIcon from '@material-ui/icons/Add'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
    deleteReport,
    fetchReports,
    fetchTenants,
    fetchTenantUsers,
    ReportModel,
    TenantUserInfo,
    updateTenantContact,
} from '../../redux/actions/dashboard'
import { Tenant } from '../../shared/types/tenant'
import { config } from '../../config'
import DashboardSection, { BUTTON_WIDTH, FIELD_GAP, HALVES_GAP, SECTION_PADDING } from './DashboardSection'
import TenantUsersTable, { useTenantTableStyles } from './TenantUsersTable'
import ReportDialog from './ReportDialog'
import TenantDialog from './TenantDialog'

const NO_TENANT_KEY = '__none__'

const useStyles = makeStyles((theme) => ({
    alert: {
        marginBottom: theme.spacing(1),
    },
    accordion: {
        '&.Mui-expanded': {
            margin: 0,
        },
        '&.Mui-expanded:before': {
            opacity: 1,
        },
    },
    summary: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: theme.spacing(1),
    },
    tenantName: {
        fontWeight: 600,
    },
    counts: {
        marginLeft: 'auto',
        display: 'flex',
        gap: theme.spacing(1),
    },
    details: {
        display: 'block',
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: `${SECTION_PADDING}px ${SECTION_PADDING}px ${theme.spacing(3)}px`,
    },
    block: {
        marginTop: theme.spacing(3),
    },
    contactRow: {
        display: 'grid',
        gridTemplateColumns: `1fr ${BUTTON_WIDTH}px`,
        alignItems: 'start',
        gap: FIELD_GAP,
        [theme.breakpoints.up('lg')]: {
            width: `calc(50% - ${HALVES_GAP / 2 + SECTION_PADDING}px)`,
        },
    },
    contactButton: {
        width: BUTTON_WIDTH,
        marginTop: 6,
    },
    url: {
        wordBreak: 'break-all',
    },
    centered: {
        padding: theme.spacing(4),
        textAlign: 'center',
    },
    footer: {
        marginTop: theme.spacing(2),
    },
    addButton: {
        width: BUTTON_WIDTH,
    },
}))

interface Props {
    onSuccess: (msg: string) => void
    onError: (msg: string) => void
}

interface TenantGroup {
    key: string
    tenant?: Tenant
    users: TenantUserInfo[]
    reports: ReportModel[]
}

const sameId = (a?: string | null, b?: string | null) => Boolean(a && b && a.toLowerCase() === b.toLowerCase())

function TenantContact({ tenant, onSaved, onError }: { tenant: Tenant; onSaved: () => void; onError: (msg: string) => void }) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [contact, setContact] = useState(tenant.contact ?? '')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setContact(tenant.contact ?? '')
    }, [tenant.contact])

    const handleSave = async () => {
        setSaving(true)
        try {
            await updateTenantContact(tenant.id, contact)
            onSaved()
        } catch {
            onError(t('dashboard.errorSavingContact'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className={classes.contactRow}>
            <TextField
                variant="filled"
                size="small"
                label={t('dashboard.contact')}
                inputProps={{ maxLength: 1000 }}
                value={contact}
                helperText=" "
                onChange={(e) => setContact(e.target.value)}
            />
            <Button
                className={classes.contactButton}
                variant="contained"
                color="primary"
                disabled={contact === (tenant.contact ?? '') || saving}
                onClick={handleSave}
            >
                {t('dashboard.save')}
            </Button>
        </div>
    )
}

export default function TenantOverview({ onSuccess, onError }: Props) {
    const classes = useStyles()
    const tableClasses = useTenantTableStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const loginTenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const tenantsValue: Tenant[] | null = useSelector((state: any) => state.tenants.value)
    const tenants: Tenant[] = useMemo(() => tenantsValue ?? [], [tenantsValue])
    const tenantsError = useSelector((state: any) => state.tenants.error)
    const reports: ReportModel[] | undefined = useSelector((state: any) => state.reports.value)

    const [users, setUsers] = useState<TenantUserInfo[]>()
    const [usersError, setUsersError] = useState<string>()
    const [expanded, setExpanded] = useState<string | false>(false)

    const [reportDialog, setReportDialog] = useState<{ report?: ReportModel; tenantId?: string | null }>()
    const [reportToDelete, setReportToDelete] = useState<ReportModel>()
    const [deleting, setDeleting] = useState(false)
    const [tenantDialogOpen, setTenantDialogOpen] = useState(false)

    const loadUsers = () => {
        fetchTenantUsers()
            .then((result) => {
                setUsers(result ?? [])
                setUsersError(undefined)
            })
            .catch((e: any) => setUsersError(e?.message || 'unknown error'))
    }

    useEffect(() => {
        if (!tenantsValue || tenantsValue.length === 0) dispatch(fetchTenants())
        loadUsers()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        dispatch(fetchReports(loginTenantId))
    }, [dispatch, loginTenantId])

    const reloadReports = () => dispatch(fetchReports(loginTenantId))

    const groups: TenantGroup[] = useMemo(() => {
        const sorted = [...tenants].sort((a, b) => {
            const aMain = sameId(a.id, config.mainTenantId) ? 0 : 1
            const bMain = sameId(b.id, config.mainTenantId) ? 0 : 1
            return aMain - bMain || (a.name ?? '').localeCompare(b.name ?? '')
        })
        const result: TenantGroup[] = sorted.map((tenant) => ({
            key: tenant.id,
            tenant,
            users: (users ?? []).filter((u) => sameId(u.tenantId, tenant.id)),
            reports: (reports ?? []).filter((r) => sameId(r.tenantId, tenant.id)),
        }))
        const orphanReports = (reports ?? []).filter((r) => !sorted.some((x) => sameId(r.tenantId, x.id)))
        if (orphanReports.length > 0) {
            result.push({ key: NO_TENANT_KEY, users: [], reports: orphanReports })
        }
        return result
    }, [tenants, users, reports])

    const handleDeleteReport = async () => {
        if (!reportToDelete?.id) return
        setDeleting(true)
        try {
            await deleteReport(reportToDelete.id)
            onSuccess(t('dashboard.reportDeleted'))
            setReportToDelete(undefined)
            reloadReports()
        } catch {
            onError(t('dashboard.errorDeletingReport'))
        } finally {
            setDeleting(false)
        }
    }

    const loading = (!tenantsValue && !tenantsError) || (!users && !usersError) || !reports

    const renderReports = (group: TenantGroup) => (
        <Table size="small" className={tableClasses.table}>
            <TableHead>
                <TableRow>
                    <TableCell>{t('reports.title')}</TableCell>
                    <TableCell>{t('reports.url')}</TableCell>
                    <TableCell>{t('common.description')}</TableCell>
                    <TableCell className={tableClasses.actionsCell}>
                        <Button
                            size="small"
                            color="primary"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => setReportDialog({ tenantId: group.tenant?.id ?? null })}
                        >
                            {t('dashboard.addReport')}
                        </Button>
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {group.reports.length === 0 ? (
                    <TableRow>
                        <TableCell colSpan={4} className={tableClasses.empty}>
                            {t('dashboard.noReports')}
                        </TableCell>
                    </TableRow>
                ) : (
                    group.reports.map((report) => (
                        <TableRow key={report.id} hover>
                            <TableCell>{report.name || t('common.none')}</TableCell>
                            <TableCell className={classes.url}>
                                {report.url ? (
                                    <a href={report.url} target="_blank" rel="noopener noreferrer">
                                        {report.url}
                                    </a>
                                ) : (
                                    t('common.none')
                                )}
                            </TableCell>
                            <TableCell>{report.description || ''}</TableCell>
                            <TableCell className={tableClasses.actionsCell}>
                                <Tooltip title={t('dashboard.editReport') as string}>
                                    <IconButton size="small" onClick={() => setReportDialog({ report })}>
                                        <EditOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={t('dashboard.deleteReport') as string}>
                                    <IconButton size="small" onClick={() => setReportToDelete(report)}>
                                        <DeleteOutline fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>
    )

    const renderGroup = (group: TenantGroup) => {
        const tenantName: string = group.tenant?.name ?? String(t('dashboard.withoutTenant'))
        const isMain = sameId(group.tenant?.id, config.mainTenantId)
        const counts = [
            group.tenant ? String(t('dashboard.adminsCountLabel', { count: group.users.length })) : '',
            String(t('dashboard.reportsCountLabel', { count: group.reports.length })),
        ].filter(Boolean)
        return (
            <Accordion
                key={group.key}
                className={classes.accordion}
                expanded={expanded === group.key}
                onChange={(_, open) => setExpanded(open ? group.key : false)}
                TransitionProps={{ unmountOnExit: true }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div className={classes.summary}>
                        <Typography className={classes.tenantName}>{tenantName}</Typography>
                        {isMain ? <Chip size="small" color="primary" label={t('dashboard.mainTenant')} /> : null}
                        <div className={classes.counts}>
                            {counts.map((label) => (
                                <Chip key={label} size="small" variant="outlined" label={label} />
                            ))}
                        </div>
                    </div>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                    {group.tenant ? (
                        <>
                            <TenantContact
                                tenant={group.tenant}
                                onSaved={() => {
                                    onSuccess(t('dashboard.contactSaved'))
                                    dispatch(fetchTenants())
                                }}
                                onError={onError}
                            />
                            <TenantUsersTable
                                users={group.users}
                                tenantId={group.tenant.id}
                                tenantName={tenantName}
                                loadError={usersError}
                                onChanged={loadUsers}
                                onSuccess={onSuccess}
                                onError={onError}
                            />
                            <div className={classes.block}>{renderReports(group)}</div>
                        </>
                    ) : (
                        renderReports(group)
                    )}
                </AccordionDetails>
            </Accordion>
        )
    }

    return (
        <DashboardSection title={t('dashboard.tenantsTitle')} plain>
            {tenantsError ? (
                <Alert severity="error" className={classes.alert}>
                    {t('dashboard.errorLoadingTenants')}
                </Alert>
            ) : null}
            {usersError ? (
                <Alert severity="error" className={classes.alert}>
                    {t('dashboard.errorLoadingTenantUsers')} ({usersError})
                </Alert>
            ) : null}
            <div>
                {loading ? (
                    <div className={classes.centered}>
                        <CircularProgress size={40} />
                    </div>
                ) : (
                    groups.map(renderGroup)
                )}
            </div>
            <div className={classes.footer}>
                <Button
                    className={classes.addButton}
                    variant="contained"
                    color="primary"
                    onClick={() => setTenantDialogOpen(true)}
                >
                    {t('dashboard.addTenant')}
                </Button>
            </div>

            <TenantDialog
                open={tenantDialogOpen}
                onClose={() => setTenantDialogOpen(false)}
                onCreated={(tenant) => {
                    setTenantDialogOpen(false)
                    onSuccess(t('dashboard.tenantCreated'))
                    dispatch(fetchTenants())
                    loadUsers()
                    setExpanded(tenant.id)
                }}
                onError={onError}
            />

            <ReportDialog
                open={Boolean(reportDialog)}
                report={reportDialog?.report}
                defaultTenantId={reportDialog?.tenantId}
                tenants={tenants}
                onClose={() => setReportDialog(undefined)}
                onSaved={() => {
                    setReportDialog(undefined)
                    onSuccess(t('dashboard.reportSaved'))
                    reloadReports()
                }}
                onError={onError}
            />

            <Dialog open={Boolean(reportToDelete)} onClose={() => !deleting && setReportToDelete(undefined)}>
                <DialogTitle>{t('dashboard.deleteReport')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('dashboard.deleteReportConfirm', { name: reportToDelete?.name })}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReportToDelete(undefined)} disabled={deleting}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleDeleteReport} disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </DashboardSection>
    )
}
